import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { tokenService } from "./TokenService";
import { BASE_URL } from "../constant/config";

interface FailedRequest {
  config: InternalAxiosRequestConfig;
  resolve: (value: AxiosResponse) => void;
  reject: (error: any) => void;
}

class AxiosService {
  private instance: AxiosInstance;
  private failedRequestsQueue: FailedRequest[] = [];
  private isRefreshing = false;
  private lastRefreshAttempt: number = 0;
  private refreshAttemptCount: number = 0;

  constructor() {
    this.instance = axios.create({
      baseURL: BASE_URL,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
      timeout: 120000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = tokenService.getToken();
        if (token) {
          config.headers = config.headers ?? {};
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        // Handle FormData by removing Content-Type header
        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor to handle 401 errors with token refresh, then logout
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
          _isRefreshCall?: boolean;
        };

        // Check if it's a 401 error and we haven't already tried to retry
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true; // Prevent infinite loops

          console.log("401 error detected on URL:", originalRequest.url);

          // Skip refresh if this is the refresh API call itself
          // Check for both URL patterns and the refresh flag
          const isRefreshCall =
            originalRequest.url?.includes("/auth/refresh") ||
            originalRequest.url?.includes("authRefresh") ||
            originalRequest._isRefreshCall ||
            originalRequest.headers?.["X-Refresh-Token-Call"];

          if (isRefreshCall) {
            console.log("401 from refresh API - performing immediate logout");
            this.performFullLogout();
            return Promise.reject(error);
          }

          // If we're already refreshing, queue this request
          if (this.isRefreshing) {
            console.log(
              "Already refreshing, queueing request for:",
              originalRequest.url,
            );
            return new Promise<AxiosResponse>((resolve, reject) => {
              this.failedRequestsQueue.push({
                config: originalRequest,
                resolve,
                reject,
              });
            });
          }

          // Additional safety check - if we have too many queued requests, something is wrong
          if (this.failedRequestsQueue.length > 50) {
            console.error(
              "Too many queued requests - forcing logout to prevent infinite loop",
            );
            this.performFullLogout();
            return Promise.reject(new Error("Too many failed requests"));
          }

          // Additional safety checks to prevent infinite refresh loops
          const now = Date.now();
          const timeSinceLastRefresh = now - this.lastRefreshAttempt;

          // If we've attempted to refresh within the last 5 seconds, skip
          if (timeSinceLastRefresh < 5000) {
            console.log("Refresh attempted too recently, forcing logout");
            this.performFullLogout();
            return Promise.reject(new Error("Refresh rate limited"));
          }

          // Reset attempt count every minute
          if (timeSinceLastRefresh > 60000) {
            this.refreshAttemptCount = 0;
          }

          // If we've attempted too many refreshes, force logout
          if (this.refreshAttemptCount >= 3) {
            console.log("Too many refresh attempts, forcing logout");
            this.performFullLogout();
            return Promise.reject(new Error("Too many refresh attempts"));
          }

          // Start token refresh process
          this.isRefreshing = true;
          this.lastRefreshAttempt = now;
          this.refreshAttemptCount++;

          try {
            const refreshSuccessful = await this.handleTokenRefresh();

            if (refreshSuccessful) {
              // Token refresh successful - reset counters and retry all queued requests
              this.refreshAttemptCount = 0;
              await this.retryFailedRequests();

              // Retry the original request with new token
              const token = tokenService.getToken();
              if (token) {
                originalRequest.headers = originalRequest.headers ?? {};
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
              }

              return this.instance(originalRequest);
            } else {
              // Token refresh failed - logout immediately
              this.rejectAllFailedRequests(new Error("Token refresh failed"));
              this.performFullLogout();
              return Promise.reject(error);
            }
          } catch (refreshError) {
            // Any error in refresh process - logout immediately
            this.rejectAllFailedRequests(refreshError);
            this.performFullLogout();
            return Promise.reject(error);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      },
    );
  }

  private async handleTokenRefresh(): Promise<boolean> {
    try {
      console.log("Starting token refresh...");

      // Import refreshToken function dynamically to avoid circular dependency
      const { refreshToken } = await import("../api/authApi");

      // Call refreshToken which should make the API call
      // We need to ensure the refresh API call is properly logged
      console.log("Calling refresh token API...");

      // Add a timeout to prevent hanging
      const refreshTimeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Refresh API timeout")), 10000); // 10 second timeout
      });

      const response = await Promise.race([refreshToken(), refreshTimeout]);
      console.log("Refresh token API response received:", response);

      if (response.success && response.accessToken) {
        // Update token in service
        tokenService.setToken(response.accessToken, response.expiresIn);
        console.log("Token refresh successful");
        return true;
      } else {
        console.warn("Token refresh failed - no valid response");
        return false;
      }
    } catch (error: any) {
      console.error("Token refresh error:", error);
      // Check if the refresh API returned 401
      if (error.response?.status === 401) {
        console.log(
          "Refresh API returned 401 - token is completely invalid, logout required",
        );
      } else {
        console.error("Other refresh error:", error);
      }
      return false;
    }
  }

  private async retryFailedRequests(): Promise<void> {
    const requestsToRetry = [...this.failedRequestsQueue];
    this.failedRequestsQueue = [];

    console.log(`Retrying ${requestsToRetry.length} failed requests`);

    // Process requests sequentially to avoid overwhelming the server
    for (const failedRequest of requestsToRetry) {
      try {
        // Add fresh token to the request
        const token = tokenService.getToken();
        if (token) {
          failedRequest.config.headers = failedRequest.config.headers ?? {};
          failedRequest.config.headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await this.instance(failedRequest.config);
        failedRequest.resolve(response);

        console.log(
          `Successfully retried request to ${failedRequest.config.url}`,
        );
      } catch (error) {
        console.error(
          `Failed to retry request to ${failedRequest.config.url}:`,
          error,
        );
        failedRequest.reject(error);
      }
    }
  }

  private rejectAllFailedRequests(error: any): void {
    const requestsToReject = [...this.failedRequestsQueue];
    this.failedRequestsQueue = [];

    console.log(`Rejecting ${requestsToReject.length} failed requests`);

    requestsToReject.forEach((failedRequest) => {
      failedRequest.reject(error);
    });
  }

  private async performFullLogout(): Promise<void> {
    try {
      console.log("Performing full logout...");

      // Step 1: Try server logout (ignore ALL errors including 401)
      try {
        const { logoutUser } = await import("../api/authApi");
        // Create a timeout for logout API call
        const logoutTimeout = new Promise<void>((_, reject) => {
          setTimeout(() => reject(new Error("Logout API timeout")), 5000); // 5 second timeout
        });

        // Race between logout and timeout, but ignore all errors
        await Promise.race([logoutUser(), logoutTimeout]);
        console.log("Server logout successful");
      } catch (error) {
        // Completely ignore logout API errors (including 401)
        console.log(
          "Server logout failed (expected if token expired), proceeding with local cleanup:",
          error,
        );
      }

      // Step 2: Clear token service
      tokenService.clearToken();

      // Step 3: Clear all auth-related data from localStorage
      try {
        localStorage.removeItem("auth_user");
        localStorage.removeItem("project_link_params");
        // Clear any other auth-related items
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (
            key &&
            (key.startsWith("auth_") ||
              key.includes("token") ||
              key.includes("user"))
          ) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
        console.log("Cleared localStorage auth data");
      } catch (error) {
        console.error("Failed to clear localStorage:", error);
      }

      // Step 4: Clear all auth-related data from sessionStorage
      try {
        const sessionKeysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (
            key &&
            (key.startsWith("auth_") ||
              key.includes("token") ||
              key.includes("user"))
          ) {
            sessionKeysToRemove.push(key);
          }
        }
        sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));
        console.log("Cleared sessionStorage auth data");
      } catch (error) {
        console.error("Failed to clear sessionStorage:", error);
      }

      // Step 5: Emit logout event (let the App component handle navigation)
      console.log("Emitting logout event...");
      window.dispatchEvent(new CustomEvent("auth:logout"));
    } catch (error) {
      console.error("Error during full logout:", error);
      // Emit logout event even on error so the app can handle it
      window.dispatchEvent(new CustomEvent("auth:logout"));
    }
  }

  // Public method to get the axios instance
  public getInstance(): AxiosInstance {
    return this.instance;
  }

  // Public method to get the current queue status
  public getQueueStatus(): FailedRequest[] {
    return this.failedRequestsQueue;
  }

  // Public method to clear the failed requests queue
  public clearQueue(): void {
    this.failedRequestsQueue = [];
  }
}

// Create and export a singleton instance
export const axiosService = new AxiosService();

// Export the axios instance for backward compatibility
export const api = axiosService.getInstance();

// Export helper function for auth token (backward compatibility)
export function getAuthToken(): string {
  const token = tokenService.getToken();
  return token ? `Bearer ${token}` : "";
}
